import MortgageAccount from "./mortgage";
import {
  PlannedInterestAccount,
  SavingsAccount,
  SavingsTransaction,
} from "./savings";
import { SimulationFormInputs, SavingsAccountParams } from "./types/forms";

export default class MortgageSimulator {
  readonly principal: number;
  readonly interestOnly: boolean;
  readonly termYears: number | undefined;
  readonly interestRate: number | undefined;
  readonly desiredMonthlyPayment: number | undefined;
  readonly additionalCapitalRepayments: boolean;
  readonly dealLength: number | undefined;
  readonly _maxAffordable: number | undefined;
  readonly savingsInterestRate: number | undefined;
  readonly additionalCapitalRepaymentPercentageLimit: number | undefined;
  readonly reduceTermOnAdditionalCapitalRepayment: boolean | undefined;
  readonly skipAdditionalCapitalRepayments: boolean | undefined;
  readonly savingsAccounts: SavingsAccountParams[];
  readonly saveAtStart: boolean | undefined;

  readonly mortgage: MortgageAccount;
  private readonly savings: SavingsAccount | undefined;
  private readonly extraAccounts: PlannedInterestAccount[];

  readonly initialParams: SimulationFormInputs;

  readonly warningMessages: string[] = [];

  requiredMonths: number | null;

  constructor(params: SimulationFormInputs) {
    const initialParams: SimulationFormInputs = {
      principal: params.principal,
      interestOnly: !!params.interestOnly,
      termYears: params.termYears,
      interestRate: params.interestRate,
      desiredMonthlyPayment: params.desiredMonthlyPayment,
      additionalCapitalRepayments: !!params.additionalCapitalRepayments,
      dealLength: params.dealLength,
      maxAffordable: params.maxAffordable,
      savingsInterestRate: params.savingsInterestRate,
      additionalCapitalRepaymentPercentageLimit:
        params.additionalCapitalRepaymentPercentageLimit,
      reduceTermOnAdditionalCapitalRepayment:
        !!params.reduceTermOnAdditionalCapitalRepayment,
      skipAdditionalCapitalRepayments: !!params.skipAdditionalCapitalRepayments,
      savingsAccounts: params.savingsAccounts,
      saveAtStart: !!params.saveAtStart,
    };

    this.initialParams = initialParams;

    this.principal = initialParams.principal!;

    this.termYears = initialParams.termYears;
    this.interestRate = initialParams.interestRate;
    this.desiredMonthlyPayment = initialParams.desiredMonthlyPayment;

    this.interestOnly = !!initialParams.interestOnly;

    this.additionalCapitalRepayments =
      !!initialParams.additionalCapitalRepayments;
    if (this.additionalCapitalRepayments) {
      this.dealLength = initialParams.dealLength!;
      this.additionalCapitalRepaymentPercentageLimit =
        initialParams.additionalCapitalRepaymentPercentageLimit;
      this.reduceTermOnAdditionalCapitalRepayment =
        !!initialParams.reduceTermOnAdditionalCapitalRepayment;
      this.skipAdditionalCapitalRepayments =
        !!initialParams.skipAdditionalCapitalRepayments;

      this.savingsInterestRate = initialParams.savingsInterestRate ?? 0;
      this.savings = new SavingsAccount({
        initialAmount: 0,
        interestRate: this.savingsInterestRate,
      });

      const maxAffordable = initialParams.maxAffordable;
      if (maxAffordable && maxAffordable <= 0)
        throw new Error("Max affordable amount must be greater than zero.");
      if (
        maxAffordable &&
        Math.round(maxAffordable * 100) / 100 !== maxAffordable
      )
        throw new Error(
          "Max affordable amount must be rounded to two decimal places.",
        );
      this._maxAffordable = initialParams.maxAffordable;
    }

    this.mortgage = new MortgageAccount(
      this.principal,
      this.termYears ? this.termYears * 12 : undefined,
      this.interestRate,
      this.desiredMonthlyPayment,
      this.dealLength,
      this.interestOnly,
      this.additionalCapitalRepaymentPercentageLimit,
      this.reduceTermOnAdditionalCapitalRepayment,
    );

    // Initialize extra savings/investment accounts
    this.savingsAccounts = initialParams.savingsAccounts ?? [];
    this.extraAccounts = (initialParams.savingsAccounts ?? []).map(
      (cfg) =>
        new PlannedInterestAccount({
          name: cfg.name ?? "",
          initialAmount: cfg.initialAmount ?? 0,
          interestRate: cfg.interestRate ?? 0,
          contributionAmount: cfg.contributionAmount ?? 0,
          contributionFrequency: cfg.contributionFrequency ?? "monthly",
          interestCompounding: cfg.interestCompounding ?? "monthly",
        }),
    );
    this.saveAtStart = !!initialParams.saveAtStart;

    this.requiredMonths = null;

    this.simulate();
  }

  copy(): MortgageSimulator {
    const copyParams: SimulationFormInputs = {
      ...this.initialParams,
      savingsAccounts: (this.initialParams.savingsAccounts ?? []).map(
        (acc) => ({ ...acc }),
      ),
    };
    return new MortgageSimulator(copyParams);
  }

  makeMonthlyPayment() {
    const payment = this.mortgage.pmt;
    if (this.savings && this.saveAtStart) {
      // monthly payment comes from savings (when ACRs are enabled)
      this.savings.withdrawForMortgage(payment);
    }
    this.mortgage.pay(payment);
  }

  reasonToMakeAdditionalCapitalRepayment(): string | null {
    if (!this.additionalCapitalRepayments) return null;
    if (this.savings && this.savings.balance <= 0) return null;
    if (this.mortgage.isBetweenFixedDeals) return "between-fixed-deals";
    if (this.mortgage.remainingAdditionalCapitalRepayments <= 0) return null;
    if (
      this.savings &&
      this.savings.balance >=
        this.mortgage.remainingAdditionalCapitalRepaymentAmount
    ) {
      return "saved-maximum-additional-capital-repayment-amount";
    }
    const estimatedYearlySavings =
      (this.maxAffordable - this.mortgage.pmt) * 12;
    if (
      estimatedYearlySavings <
      this.mortgage.additionalCapitalRepaymentAllowanceAmount
    ) {
      if (
        this.mortgage.dealDistributedAdditionalCapitalRepaymentMonths.includes(
          this.mortgage.currentDealMonth,
        )
      ) {
        return "spread-additional-capital-repayments-over-deal";
      }
    } else if (this.mortgage.currentYearlyCycleMonth === 0) {
      return "use-additional-capital-repayment-allowance-before-year-end";
    }
    return null;
  }

  makeAdditionalCapitalRepayment() {
    // Skip making ACR if the setting is enabled
    if (this.skipAdditionalCapitalRepayments) return;

    const reason = this.reasonToMakeAdditionalCapitalRepayment();
    if (!reason) return;
    const repayment =
      Math.round(
        Math.min(
          this.mortgage.remainingAdditionalCapitalRepaymentAmount,
          this.savings!.balance,
        ) * 100,
      ) / 100;
    if (repayment <= 0) return;
    this.mortgage.pay(repayment, true, reason);
    this.savings!.withdrawForMortgage(repayment);
    this.mortgage.adjustRemainingMonthsForAdditionalRepayment();
  }

  get monthsPaidOffSooner(): number | null {
    if (!this.additionalCapitalRepayments) return null;
    if (this.mortgage.principal > 0) return null;
    return this.mortgage.termMonths - this.mortgage.ledger.length;
  }

  get totalInterestPaid(): number {
    const val = this.mortgage.ledger.entries.reduce(
      (acc, entry) => acc + entry.interestAmount,
      0,
    );
    return Math.round(val * 100) / 100;
  }

  get totalInterestEarned(): number {
    const transactions = this.savingsTransactions;
    let val = 0;
    Object.values(transactions).forEach((accountTransactions) => {
      accountTransactions.forEach((tx) => {
        val += tx.interestAdded;
      });
    });
    return Math.round(val * 100) / 100;
  }

  get totalCostOfMortgage(): number {
    return (
      Math.round(
        (this.totalInterestPaid - this.totalInterestEarned + this.principal) *
          100,
      ) / 100
    );
  }

  get savingsTransactions(): { [accountIndex: number]: SavingsTransaction[] } {
    const result: { [accountIndex: number]: SavingsTransaction[] } = {};
    if (this.savings) {
      let mainAccountTransactions = this.savings.transactions;

      const requiredMonths =
        (this.requiredMonths !== null
          ? this.requiredMonths
          : this.mortgage.ledger.length) + 1;
      let previousBalance =
        mainAccountTransactions[mainAccountTransactions.length - 1]
          .balanceAfter;
      // Adjust for any final additional capital repayment taken from savings
      if (
        this.mortgage.ledger.entries[this.mortgage.ledger.entries.length - 1]
          .additionalCapitalRepayments
      ) {
        previousBalance -=
          this.mortgage.ledger.entries[this.mortgage.ledger.entries.length - 1]
            .additionalCapitalRepayments;
      }
      const tempAccount = new SavingsAccount({
        initialAmount: previousBalance,
        interestRate: this.savings.interestRate,
      });
      for (
        let month = mainAccountTransactions.length;
        month < requiredMonths;
        month++
      ) {
        const receipt = tempAccount.addMonthlySavings(this.maxAffordable);
        tempAccount.finalizeMonth(receipt);
      }
      // Fix month numbering for extended transactions
      const extendedTransactions = tempAccount.transactions.map((tx, idx) => ({
        ...tx,
        month: mainAccountTransactions.length + idx + 1,
      }));
      mainAccountTransactions =
        mainAccountTransactions.concat(extendedTransactions);

      result[-1] = mainAccountTransactions;
    }
    this.extraAccounts.forEach((account, index) => {
      const nMonths =
        this.requiredMonths !== null
          ? this.requiredMonths
          : this.mortgage.ledger.length;
      result[index] = account.transactions(nMonths);
    });
    return result;
  }

  totalInterestEarnedInAccount(accountIndex: number): number {
    const transactions = this.savingsTransactions[accountIndex];
    let val = 0;
    transactions.forEach((tx) => {
      val += tx.interestAdded;
    });
    return Math.round(val * 100) / 100;
  }

  totalInterestEarnedInReserveAccount(): number {
    return this.totalInterestEarnedInAccount(-1);
  }

  get numberOfMonths(): number {
    return this.mortgage.ledger.length;
  }

  get maxAffordable(): number {
    return this._maxAffordable!;
  }

  simulate() {
    while (this.mortgage.principal > 0) {
      let savingsReceipt = null;

      // Handle mortgage-linked savings (budget cash buffer)
      if (this.savings) {
        if (this.saveAtStart) {
          // Add full maxAffordable at start, interest calculated on full balance
          savingsReceipt = this.savings.addMonthlySavings(
            this.maxAffordable,
            true,
          );
          this.mortgage.startMonth(savingsReceipt);
        } else {
          // Calculate net amount to save (after mortgage payment)
          // For first month, use current pmt; for subsequent months, use last entry's payment
          let monthlyPayment = this.mortgage.pmt;
          if (this.mortgage.ledger.entries.length > 0) {
            const lastEntry =
              this.mortgage.ledger.entries[
                this.mortgage.ledger.entries.length - 1
              ];
            monthlyPayment = lastEntry.monthlyPayment;
          }
          const netSavings =
            Math.round((this.maxAffordable - monthlyPayment) * 100) / 100;
          // Interest calculated on previous balance, then net amount added at end
          savingsReceipt = this.savings.addMonthlySavings(netSavings, false);
          this.mortgage.startMonth(savingsReceipt);
        }
      } else {
        this.mortgage.startMonth();
      }

      this.makeMonthlyPayment();
      this.makeAdditionalCapitalRepayment();

      // Finalize savings transaction for the month (before endMonth so we capture all transactions)
      if (this.savings && savingsReceipt) {
        this.savings.finalizeMonth(savingsReceipt);
      }

      this.mortgage.endMonth();

      if (this.interestOnly && this.mortgage.remainingMonths === 0) {
        // Get main savings account balance if it exists
        let totalSavingsBalance =
          this.savingsTransactions.hasOwnProperty(-1) &&
          this.savingsTransactions[-1].length > 0
            ? this.savingsTransactions[-1][
                this.savingsTransactions[-1].length - 1
              ].balanceAfter
            : 0;

        // Sum balances of other accounts
        Object.entries(this.savingsTransactions).forEach(
          ([accountIndexStr, accountTransactions]) => {
            const accountIndex = Number(accountIndexStr);
            if (accountIndex === -1) return; // skip main savings account
            if (accountTransactions.length === 0) return; // skip empty accounts
            totalSavingsBalance +=
              accountTransactions[accountTransactions.length - 1].balanceAfter;
          },
        );

        if (totalSavingsBalance < this.mortgage.principal) {
          this.warningMessages.push(
            "Savings balance is insufficient to pay off the mortgage principal at the end of the interest-only period.",
          );
        }

        break;
      }

      if (this.mortgage.ledger.length > 1000) {
        throw new Error(
          "Simulation exceeded 1000 months, something is probably wrong.",
        );
      }
    }
    this.extraAccounts.forEach((account) => {
      // Pre-simulate extra accounts to the end of the mortgage
      account.balance(this.mortgage.ledger.length);
    });
  }
}

export class MortgageSimulatorGroup {
  readonly simulations: MortgageSimulator[];
  equalLength: boolean;

  constructor(equalLength: boolean) {
    this.simulations = [];
    this.equalLength = equalLength;
  }

  setEqualLength(equalLength: boolean) {
    this.equalLength = equalLength;
    this.refresh();
  }

  get length(): number {
    return this.simulations.length;
  }

  getSimulation(index: number): MortgageSimulator {
    return this.simulations[index];
  }

  refresh() {
    if (this.length > 1 && this.equalLength) {
      const maxLength = Math.max(
        ...this.simulations.map((sim) => sim.numberOfMonths),
      );
      this.simulations.forEach((sim) => {
        sim.requiredMonths = maxLength;
      });
    } else {
      this.simulations.forEach((sim) => {
        sim.requiredMonths = null;
      });
    }
  }

  addSimulation(simulation: MortgageSimulator): void {
    this.simulations.forEach((sim) => {
      sim.requiredMonths = null;
    });
    this.simulations.push(simulation);
    this.refresh();
  }

  replaceSimulation(index: number, simulation: MortgageSimulator): void {
    if (index < 0 || index >= this.simulations.length) {
      throw new Error("Index out of bounds.");
    }
    this.simulations[index] = simulation;
    this.refresh();
  }

  deleteSimulation(index: number): void {
    if (index < 0 || index >= this.simulations.length) {
      throw new Error("Index out of bounds.");
    }
    this.simulations.splice(index, 1);
    this.refresh();
  }

  duplicateSimulation(index: number): void {
    if (index < 0 || index >= this.simulations.length) {
      throw new Error("Index out of bounds.");
    }
    const simulation = this.simulations[index].copy();
    this.simulations.push(simulation);
    this.refresh();
  }

  moveSimulation(fromIndex: number, toIndex: number): void {
    if (
      fromIndex < 0 ||
      fromIndex >= this.simulations.length ||
      toIndex < 0 ||
      toIndex >= this.simulations.length
    ) {
      throw new Error("Index out of bounds.");
    }
    if (fromIndex === toIndex) return;

    const [removed] = this.simulations.splice(fromIndex, 1);
    this.simulations.splice(toIndex, 0, removed);
    // No need to refresh since lengths haven't changed
  }

  moreExpensiveBy(index: number): number | null {
    if (this.simulations.length <= 1) {
      return null;
    }
    const cheapest = this.simulations.reduce(
      (min, sim) => Math.min(min, sim.totalCostOfMortgage),
      Infinity,
    );
    return (
      Math.round(
        (this.simulations[index].totalCostOfMortgage - cheapest) * 100,
      ) / 100
    );
  }
}
